import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, where, onSnapshot, deleteDoc, doc, setDoc, serverTimestamp, writeBatch, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useTranslation } from 'react-i18next';

// --- INTERFACES ---
interface AttendanceLog {
    id: string;
    studentId: string;
    tripName: string;
    status?: string;
    isPresent?: boolean;
    timestamp?: any;
    routeDescription?: string;
    date?: string;
    isLocked?: boolean;
    busNumber?: string;
}

interface Student {
    id: string;
    name?: string;
    studentClass?: string;
    assignedBusNumber?: string;
    searchTags?: string[];
    isPresent?: boolean;
    morningIsPresent?: boolean;
    noonIsPresent?: boolean;
    afternoonIsPresent?: boolean;
    eveningIsPresent?: boolean;
    address?: string;
    [key: string]: any;
}

interface Bus {
    id: string;
    number: string | number;
    routeDescription?: string;
    isTracking?: boolean;
}

interface AttendanceReportProps {
    schoolId: string;
}

// 🔧 Mapping entre les périodes et les champs Firestore
const getPresenceFieldForTrip = (tripKey: string): string => {
    const mapping: { [key: string]: string } = {
        'morningtrip': 'morningIsPresent',
        'noontrip': 'noonIsPresent',
        'afternoontrip': 'afternoonIsPresent',
        'eveningtrip': 'eveningIsPresent'
    };
    return mapping[tripKey.toLowerCase()] || 'isPresent';
};

const AttendanceReport: React.FC<AttendanceReportProps> = ({ schoolId }) => {
    console.log("🚀🚀🚀 ATTENDANCE REPORT MONTÉ !");
    console.log("📌 schoolId reçu =", schoolId);
    console.log("📌 type de schoolId =", typeof schoolId);
    
    const { t, i18n } = useTranslation();
    const [records, setRecords] = useState<any[]>([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [filterBus, setFilterBus] = useState<string>('');
    const [filterPeriod, setFilterPeriod] = useState<string>('all'); 
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [busesState, setBusesState] = useState<Bus[]>([]);
    
    const [pendingActions, setPendingActions] = useState<Record<string, { status: string, isLocked: boolean }>>({});
    const prevTrackingRef = useRef<Record<string, boolean>>({});
    
    // 🔧 Force le refresh quand les présences changent
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const periods = ['morningtrip', 'noontrip', 'afternoontrip', 'eveningtrip'];

    const getCurrentPeriodByTime = () => {
        const now = new Date();
        const hour = now.getHours();
        const minutes = now.getMinutes();
        const currentTime = hour + minutes / 60; 
        if (currentTime >= 6.5 && currentTime < 10) return 'morningtrip';
        if (currentTime >= 10 && currentTime < 13) return 'noontrip';
        if (currentTime >= 13 && currentTime < 15) return 'afternoontrip';
        if (currentTime >= 15 && currentTime < 19) return 'eveningtrip';
        return null;
    };

    const getDayNameFr = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
        return days[date.getDay()];
    };

    // --- LOGIQUE DE VERROUILLAGE GLOBALE ---
    const lockAttendanceInDB = async (busNumber: string, period: string, currentRecords: any[]) => {
        const batch = writeBatch(db);
        let hasUpdates = false;

        currentRecords.forEach(record => {
            if (String(record.busNumber) === String(busNumber) && 
                record.tripName.toLowerCase() === period.toLowerCase() && 
                !record.isLocked) {
                
                const logId = `${record.studentId}_${selectedDate}_${record.tripName}`;
                const docRef = doc(db, "schools", schoolId, "attendance_history", logId);

                batch.set(docRef, {
                    studentId: String(record.studentId),
                    studentName: record.studentName,
                    tripName: record.tripName,
                    status: record.status, 
                    isPresent: record.status === 'present',
                    date: selectedDate,
                    busNumber: String(busNumber),
                    isLocked: true, 
                    timestamp: record.timestamp?.seconds ? record.timestamp : serverTimestamp() 
                }, { merge: true });
                
                hasUpdates = true;
            }
        });

        if (hasUpdates) {
            try {
                await batch.commit();
                console.log("🔒 Données figées en base de données.");
            } catch (e) {
                console.error("Erreur de verrouillage:", e);
            }
        }
    };

    const handleManualAction = async (record: any, status: 'present' | 'absent') => {
        const rowId = record.id;
        const logId = `${record.studentId}_${selectedDate}_${record.tripName}`;
        const docRef = doc(db, "schools", schoolId, "attendance_history", logId);

        setPendingActions(prev => ({ ...prev, [rowId]: { status, isLocked: false } }));

        try {
            await setDoc(docRef, {
                studentId: String(record.studentId),
                studentName: record.studentName,
                tripName: record.tripName,
                status: status,
                isPresent: status === 'present',
                date: selectedDate,
                timestamp: serverTimestamp(), 
                busNumber: String(filterBus),
                isLocked: false 
            }, { merge: true });

            setTimeout(() => {
                setPendingActions(prev => {
                    const next = { ...prev };
                    delete next[rowId];
                    return next;
                });
            }, 1000);

        } catch (error) {
            console.error("Erreur action:", error);
        }
    };

    const handleDeleteRecord = async (logId: string) => {
        if (!window.confirm(t('confirm_delete'))) return;
        try {
            await deleteDoc(doc(db, "schools", schoolId, "attendance_history", logId));
        } catch (error) { console.error(error); }
    };

    // 🔧 Écoute les changements des présences des étudiants et force le refresh
    useEffect(() => {
        if (!schoolId || !filterBus) return;
        
        const studentsRef = collection(db, "schools", schoolId, "students");
        const q = query(studentsRef, where("assignedBusNumber", "==", String(filterBus)));
        
        const unsubscribe = onSnapshot(q, () => {
            console.log("🔄 RefreshTrigger déclenché - changement détecté dans les étudiants");
            setRefreshTrigger(prev => prev + 1);
        });
        
        return () => unsubscribe();
    }, [schoolId, filterBus]);

    // --- LOGIQUE DE SECURITE ---
    useEffect(() => {
        const currentBus = busesState.find(b => String(b.number) === String(filterBus));
        if (!currentBus) return;

        const isTracking = currentBus.isTracking || false;
        const previousIsTracking = prevTrackingRef.current[filterBus] || false;
        const activePeriod = getCurrentPeriodByTime();
        const isToday = selectedDate === new Date().toISOString().split('T')[0];

        if (isToday && activePeriod && records.length > 0) {
            if (activePeriod === 'morningtrip' || activePeriod === 'eveningtrip') {
                if (!isTracking && previousIsTracking) {
                    lockAttendanceInDB(filterBus, activePeriod, records);
                }
            } 
            else {
                if (isTracking && !previousIsTracking) {
                    lockAttendanceInDB(filterBus, activePeriod, records);
                }
            }
        }
        prevTrackingRef.current[filterBus] = isTracking;
    }, [busesState, filterBus, records, selectedDate]);

    // 🔧 Écoute les bus et sélectionne le premier disponible
    useEffect(() => {
        if (!schoolId) return;
        return onSnapshot(collection(db, "schools", schoolId, "buses"), (snap) => {
            const buses = snap.docs.map(d => ({ id: d.id, ...d.data() } as Bus));
            console.log("🚌 BUS CHARGÉS:", buses);
            console.log("🚌 NOMBRE DE BUS:", buses.length);
            setBusesState(buses);
            
            if (buses.length > 0 && filterBus === '') {
                console.log("🚌 PREMIER BUS SÉLECTIONNÉ:", buses[0].number);
                setFilterBus(String(buses[0].number));
            }
        });
    }, [schoolId]);

    // 🔧 Charge les étudiants et l'historique
    useEffect(() => {
        if (!schoolId || !filterBus) {
            console.log("⏭️ SKIP: schoolId ou filterBus manquant", { schoolId, filterBus });
            return;
        }
        
        console.log("🔍 CHARGEMENT DES ÉTUDIANTS pour bus:", filterBus);
        setLoading(true);
        
        const dayName = getDayNameFr(selectedDate).toLowerCase();
        const qStudents = query(
            collection(db, "schools", schoolId, "students"), 
            where("assignedBusNumber", "==", String(filterBus)),
            limit(100)
        );
        const qHistory = query(collection(db, "schools", schoolId, "attendance_history"), where("date", "==", selectedDate));

        const unsubStudents = onSnapshot(qStudents, (studentSnap) => {
            const students = studentSnap.docs.map(d => ({ id: d.id, ...d.data() } as Student));
            console.log("📚 ÉTUDIANTS CHARGÉS:", students.length, students.map(s => ({ name: s.name, bus: s.assignedBusNumber })));
            
            // ✅ OPTIMISATION : getDocs au lieu de onSnapshot pour l'historique
            const loadHistoryOnce = async () => {
                const historySnapshot = await getDocs(qHistory);
                const logs = historySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as AttendanceLog));
                const currentActivePeriod = getCurrentPeriodByTime();
                const isToday = selectedDate === new Date().toISOString().split('T')[0];
                const currentBus = busesState.find(b => String(b.number) === String(filterBus));

                let merged: any[] = [];

                students.forEach((student: Student) => {
                    console.log("🔍 TRAITEMENT ÉTUDIANT:", student.name);
                    console.log("🔍 Champs de présence:", {
                        morningIsPresent: student.morningIsPresent,
                        noonIsPresent: student.noonIsPresent,
                        afternoonIsPresent: student.afternoonIsPresent,
                        eveningIsPresent: student.eveningIsPresent
                    });
                    
                    const targetPeriods = filterPeriod === 'all' ? periods : [filterPeriod];
                    
                    targetPeriods.forEach(pKey => {
                        const rowId = `${student.id}_${pKey}`;
                        const requiredTag = `BUS_${filterBus}_${dayName}_${pKey.toLowerCase()}`;
                        if (!student.searchTags?.includes(requiredTag)) return;

                        const log = logs.find((l: AttendanceLog) => 
                            String(l.studentId) === String(student.id) && 
                            String(l.tripName).toLowerCase() === pKey.toLowerCase()
                        );

                        const isBusInRoute = currentBus?.isTracking === true;
                        const isTargetingCurrentPeriod = pKey.toLowerCase() === currentActivePeriod;
                        
                        let isLocked = log?.isLocked === true;
                        if (!isLocked && isToday && isTargetingCurrentPeriod) {
                            if (pKey === 'noontrip' || pKey === 'afternoontrip') {
                                isLocked = isBusInRoute;
                            }
                        }

                        let finalStatus = 'absent';
                        let currentLogId = log?.id || null;

                        if (pendingActions[rowId]) {
                            finalStatus = pendingActions[rowId].status;
                            currentLogId = 'pending';
                        } 
                        else if (log) {
                            finalStatus = log.status || (log.isPresent ? 'present' : 'absent');
                            console.log(`📅 HISTORIQUE trouvé pour ${student.name} - ${pKey} le ${selectedDate}: ${finalStatus}`);
                        } 
                        else if (isToday) {
                            const presenceField = getPresenceFieldForTrip(pKey);
                            const fieldValue = student[presenceField];
                            console.log(`🔍 Temps réel pour ${student.name} - ${pKey}: ${presenceField}=${fieldValue}`);
                            if (fieldValue === true) {
                                finalStatus = 'present';
                            }
                        }
                        else {
                            console.log(`📅 Pas d'historique pour ${student.name} - ${pKey} le ${selectedDate}`);
                        }

                        console.log(`🔍 Résultat final pour ${student.name} - ${pKey}: ${finalStatus}`);

                        merged.push({
                            id: rowId,
                            studentId: student.id,
                            logId: currentLogId, 
                            studentName: student.name || t('unknown'),
                            studentClass: student.studentClass || "---",
                            busNumber: filterBus,
                            routeDescription: currentBus?.routeDescription || student.address || t('no_route'),
                            tripName: pKey,
                            status: finalStatus,
                            isLocked: isLocked,
                            timestamp: log?.timestamp || (finalStatus === 'present' && isToday ? { toDate: () => new Date() } : null)
                        });
                    });
                });
                console.log("📊 RECORDS FINAUX:", merged);
                setRecords(merged);
                setLoading(false);
            };
            
            loadHistoryOnce();
        });
        return () => unsubStudents();
    }, [schoolId, selectedDate, filterBus, filterPeriod, busesState, pendingActions, t, refreshTrigger]);

    const filteredRecords = useMemo(() => {
        return records.filter(r => r.studentName.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [records, searchTerm]);

    const getTripLabelTranslated = (tripKey: string) => {
        const labels: { [key: string]: string } = {
            'morningtrip': `☀️ ${t('morning')}`, 'noontrip': `🍴 ${t('noon')}`, 
            'afternoontrip': `🏫 ${t('afternoon')}`, 'eveningtrip': `🌙 ${t('evening')}`, 'all': `📅 ${t('all_day')}`
        };
        return labels[tripKey.toLowerCase()] || tripKey;
    };

    const exportToCSV = () => {
        if (filteredRecords.length === 0) return;
        const headers = [t('student_name'), t('class'), t('bus'), t('route'), t('period'), t('status'), t('time')];
        const rows = filteredRecords.map(r => [
            r.studentName, r.studentClass, `Bus ${r.busNumber}`, r.routeDescription,
            getTripLabelTranslated(r.tripName).replace(/[^a-zA-Zà-ÿ ]/g, ""),
            r.status === 'present' ? t('present') : t('absent'),
            r.timestamp ? (r.timestamp.toDate ? r.timestamp.toDate().toLocaleTimeString() : new Date(r.timestamp).toLocaleTimeString()) : "--:--"
        ]);
        const csvContent = "\ufeff" + [headers, ...rows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${t('report')}_${selectedDate}.csv`;
        link.click();
    };

    const headerCellStyle: React.CSSProperties = { backgroundColor: '#FFD700', color: '#000', padding: '18px 15px', border: '1px solid #ddd', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '13px', textAlign: 'center' };
    const cellStyle: React.CSSProperties = { textAlign: 'center', padding: '15px 12px', border: '1px solid #eee', fontSize: '16px' };
    const inputStyle: React.CSSProperties = { padding: '12px', borderRadius: '5px', border: '1px solid #ccc', textAlign: 'center', fontSize: '15px' };

    return (
        <div style={{ padding: '30px', backgroundColor: '#fdfdfd', minHeight: '100vh', direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 2px 15px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <h2 style={{ fontSize: '24px', margin: 0, color: '#2c3e50', borderLeft: i18n.language === 'ar' ? 'none' : '6px solid #FFD700', borderRight: i18n.language === 'ar' ? '6px solid #FFD700' : 'none', paddingLeft: '15px', paddingRight: '15px' }}>
                            {t('attendance_report')}
                        </h2>
                        {busesState.find(b => String(b.number) === String(filterBus))?.isTracking && (
                            <span style={{ backgroundColor: '#e1f5fe', color: '#01579b', padding: '5px 12px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold', border: '1px solid #01579b' }}>
                                🚌 {t('bus_in_route', 'BUS EN ROUTE')}
                            </span>
                        )}
                    </div>
                    <button onClick={exportToCSV} style={{ backgroundColor: '#28a745', color: 'white', padding: '12px 25px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' }}>
                        📥 {t('export_csv')}
                    </button>
                </div>

                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', textAlign: 'center' }}>{t('search_student')}</label>
                        <input type="text" placeholder={`${t('search')}...`} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ ...inputStyle, minWidth: '300px' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', textAlign: 'center' }}>{t('date')}</label>
                        <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', textAlign: 'center' }}>{t('bus')}</label>
                        <select value={filterBus} onChange={(e) => setFilterBus(e.target.value)} style={{ ...inputStyle, minWidth: '200px' }}>
                            {busesState.map(bus => (
                                <option key={bus.id} value={bus.number}>{t('bus')} {bus.number}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', textAlign: 'center' }}>{t('period')}</label>
                        <select value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)} style={{ ...inputStyle, minWidth: '200px' }}>
                            <option value="all">{getTripLabelTranslated('all')}</option>
                            {periods.map(p => <option key={p} value={p}>{getTripLabelTranslated(p)}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={headerCellStyle}>{t('student_name')}</th>
                            <th style={headerCellStyle}>{t('class')}</th>
                            <th style={headerCellStyle}>{t('bus_route')}</th>
                            <th style={headerCellStyle}>{t('period')}</th>
                            <th style={headerCellStyle}>{t('actual_status')}</th>
                            <th style={headerCellStyle}>{t('time')}</th>
                            <th style={headerCellStyle}>{t('actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRecords.map((record) => (
                            <tr key={record.id} style={{ borderBottom: '1px solid #f1f1f1', opacity: record.isLocked ? 0.7 : 1 }}>
                                <td style={cellStyle}><strong>{record.studentName}</strong></td>
                                <td style={cellStyle}>{record.studentClass}</td>
                                <td style={cellStyle}>
                                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>🚍 {record.busNumber}</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>{record.routeDescription}</div>
                                </td>
                                <td style={cellStyle}>{getTripLabelTranslated(record.tripName)}</td>
                                <td style={cellStyle}>
                                    <span style={{ 
                                        backgroundColor: record.status === 'present' ? '#e8f5e9' : '#ffebee',
                                        color: record.status === 'present' ? '#2e7d32' : '#c62828',
                                        padding: '8px 15px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold'
                                    }}>
                                        {record.status === 'present' ? t('present_caps') : t('absent_caps')}
                                        {record.isLocked && " 🔒"}
                                    </span>
                                </td>
                                <td style={cellStyle}>
                                    {record.timestamp ? (
                                        (record.timestamp.toDate ? record.timestamp.toDate() : new Date(record.timestamp))
                                        .toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                                    ) : '--:--'}
                                </td>
                                <td style={cellStyle}>
                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                        {record.isLocked ? (
                                            <span style={{ color: '#2e7d32', fontWeight: 'bold', fontSize: '12px' }}>🔒 {t('finalized')}</span>
                                        ) : record.logId && record.logId !== 'pending' ? (
                                            <button onClick={() => handleDeleteRecord(record.logId)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}>🗑️</button>
                                        ) : record.logId === 'pending' ? (
                                            <span style={{ color: '#f39c12', fontSize: '12px' }}>⏳...</span>
                                        ) : (
                                            <>
                                                <button onClick={() => handleManualAction(record, 'present')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>✅</button>
                                                <button onClick={() => handleManualAction(record, 'absent')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>❌</button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AttendanceReport;